// Serviço de histórico de conversas — salva localmente no navegador (localStorage)
// Cada PERSONAGEM × MODO tem sua própria conversa.
// Estrutura: chat_history_{userId} = {
//   'salomao_chat': [...], 'salomao_learn': [...],
//   'daniel_chat':  [...], 'daniel_learn':  [...],
//   'ester_chat':   [...], 'ester_learn':   [...],
//   'davi_chat':    [...], 'davi_learn':    [...]
// }
import { ReadObject, SaveObject } from './storage';

const HISTORY_KEY = 'chat_history';

export const RETENTION_DAYS_FREE = 2;            // Grátis: mantém 2 dias
export const MAX_MESSAGES_PER_CONVERSATION = 200; // Limite por personagem+modo

export const VALID_CHARACTERS = ['salomao', 'daniel', 'ester', 'davi'];
export const VALID_MODES = ['chat', 'learn']; // chat = Bate-papo, learn = Praticar Idiomas

/** Chave do localStorage para o usuário */
const getKey = (userId) => `${HISTORY_KEY}_${userId}`;

/** Chave da conversa (personagem + modo) */
const buildConversationKey = (characterId, mode) => `${characterId}_${mode}`;

/** Valida se a combinação personagem + modo é aceita */
const isValidPair = (characterId, mode) =>
    VALID_CHARACTERS.includes(characterId) && VALID_MODES.includes(mode);

/**
 * Retorna o histórico completo do usuário (todas as conversas)
 */
export const getAllHistory = (userId) => {
    try {
        if (!userId) return {};
        const stored = ReadObject(getKey(userId));
        if (!stored || typeof stored !== 'object') return {};
        return stored;
    } catch (error) {
        console.error('Erro ao ler histórico:', error);
        return {};
    }
};

/**
 * Obtém mensagens armazenadas de uma conversa específica
 * @param {string} userId
 * @param {string} characterId - 'salomao' | 'daniel' | 'ester' | 'davi'
 * @param {string} mode - 'chat' | 'learn'
 */
export const getStoredMessages = (userId, characterId, mode) => {
    try {
        if (!userId || !isValidPair(characterId, mode)) return [];
        const history = getAllHistory(userId);
        const key = buildConversationKey(characterId, mode);
        return Array.isArray(history[key]) ? history[key] : [];
    } catch (error) {
        console.error('Erro ao obter mensagens:', error);
        return [];
    }
};

/**
 * Salva uma nova mensagem no histórico de uma conversa específica
 */
export const saveMessage = (userId, characterId, mode, message) => {
    try {
        if (!userId || !isValidPair(characterId, mode) || !message) return;

        const history = getAllHistory(userId);
        const key = buildConversationKey(characterId, mode);
        const existing = Array.isArray(history[key]) ? history[key] : [];

        const messageToSave = {
            sender: message.sender,
            message: message.message,
            timestamp: message.timestamp || new Date().toISOString()
        };

        // Adiciona ao final e aplica FIFO se passar do limite
        const updated = [...existing, messageToSave].slice(-MAX_MESSAGES_PER_CONVERSATION);

        history[key] = updated;
        SaveObject(getKey(userId), history);
    } catch (error) {
        console.error('Erro ao salvar mensagem:', error);
    }
};

/**
 * Apaga o histórico de uma conversa específica
 */
export const deleteConversation = (userId, characterId, mode) => {
    try {
        if (!userId || !isValidPair(characterId, mode)) return;
        const history = getAllHistory(userId);
        const key = buildConversationKey(characterId, mode);
        delete history[key];
        SaveObject(getKey(userId), history);
    } catch (error) {
        console.error('Erro ao apagar conversa:', error);
    }
};

/**
 * Apaga TODAS as conversas do usuário (todos os personagens e modos)
 */
export const deleteAllHistory = (userId) => {
    try {
        if (!userId) return;
        SaveObject(getKey(userId), {});
    } catch (error) {
        console.error('Erro ao apagar todo o histórico:', error);
    }
};

/**
 * Remove mensagens mais antigas que daysToKeep dias (de todas as conversas)
 */
export const pruneOldMessages = (userId, daysToKeep) => {
    try {
        if (!userId || !daysToKeep || daysToKeep <= 0) return;

        const history = getAllHistory(userId);
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - daysToKeep);
        const cutoffMs = cutoff.getTime();

        let anyChange = false;
        for (const key of Object.keys(history)) {
            const before = history[key]?.length || 0;
            const filtered = (history[key] || []).filter(msg => {
                const t = new Date(msg.timestamp).getTime();
                return !isNaN(t) && t >= cutoffMs;
            });
            if (filtered.length !== before) {
                anyChange = true;
                history[key] = filtered;
            }
        }

        if (anyChange) {
            SaveObject(getKey(userId), history);
        }
    } catch (error) {
        console.error('Erro ao pruning de mensagens antigas:', error);
    }
};

/**
 * Retorna metadata útil sobre as conversas (para a tela de gerenciamento)
 * Retorna array de objetos: [{ characterId, mode, count, lastTimestamp }, ...]
 */
export const getHistoryStats = (userId) => {
    try {
        const history = getAllHistory(userId);
        const stats = [];
        for (const characterId of VALID_CHARACTERS) {
            for (const mode of VALID_MODES) {
                const key = buildConversationKey(characterId, mode);
                const msgs = history[key] || [];
                stats.push({
                    characterId,
                    mode,
                    count: msgs.length,
                    lastTimestamp: msgs.length ? msgs[msgs.length - 1].timestamp : null
                });
            }
        }
        return stats;
    } catch (error) {
        console.error('Erro ao calcular estatísticas:', error);
        return [];
    }
};
